import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus } from "lucide-react";
import { Toaster } from "sonner";
import { api } from "../../lib/api";
import AddRecordDialog from "../../components/AddRecordDialog";

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const params = q.trim() ? { q: q.trim() } : {};
    api.get("/admin/customers", { params }).then((r) => setCustomers(r.data.customers));
  }, [q, reload]);

  return (
    <div className="p-4 sm:p-6 md:p-10" data-testid="admin-customers-page">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <span className="label-eyebrow">CRM</span>
          <h1 className="mt-2 text-3xl font-display font-medium tracking-tight text-[#121C17]">Customers <span className="text-[#5C6661] text-base ml-1">({customers.length})</span></h1>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6661]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="customers-search"
              className="bg-white border border-[#E5E2DC] rounded-full pl-9 pr-4 py-2.5 text-sm focus:border-[#1E4D3B] w-full sm:w-64" />
          </div>
          <button onClick={() => setAddOpen(true)} data-testid="customers-add-button"
            className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2">
            <Plus size={14} /> Add customer
          </button>
        </div>
      </div>

      <div className="bento-card overflow-hidden" data-testid="customers-table">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-xs label-eyebrow border-b border-[#E5E2DC] bg-[#F3F1EC]">
          <span className="col-span-2">Cust ID</span>
          <span className="col-span-3">Name</span>
          <span className="col-span-3">Service</span>
          <span className="col-span-2">Paid / Due</span>
          <span className="col-span-2">Status</span>
        </div>
        {customers.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#5C6661]">No customers yet. Add one above or convert a lead.</div>
        ) : (
          customers.map((c) => (
            <Link to={`/admin/customers/${c.id}`} key={c.id} data-testid={`customer-row-${c.id}`}
              className="block md:grid md:grid-cols-12 gap-3 px-4 sm:px-5 py-4 border-b border-[#E5E2DC] last:border-0 hover:bg-[#FDFCFB] md:items-center">
              {/* Mobile card */}
              <div className="md:hidden">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-[#121C17] truncate">{c.full_name}</span>
                  <span className={`text-[10px] px-2 py-1 rounded-full shrink-0 ${c.service_status === "Active" ? "pill-completed" : "pill-pending"}`}>{c.service_status}</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-xs">
                  <span className="text-[#5C6661] truncate">{c.service_history?.[0]?.service_name}</span>
                  <span className="font-mono text-[#5C6661] shrink-0 ml-2">{c.ref}</span>
                </div>
                <div className="mt-1 text-xs flex items-baseline gap-1">
                  <span className="text-emerald-700 font-medium">₹{(c.total_paid || 0).toLocaleString("en-IN")} paid</span>
                  <span className="text-[#5C6661]">·</span>
                  <span className="text-amber-700 font-medium">₹{(c.total_due || 0).toLocaleString("en-IN")} due</span>
                </div>
              </div>
              {/* Desktop row */}
              <span className="hidden md:inline col-span-2 font-mono text-xs text-[#5C6661]">{c.ref}</span>
              <span className="hidden md:inline col-span-3 font-medium text-[#121C17]">{c.full_name}</span>
              <span className="hidden md:inline col-span-3 text-sm text-[#5C6661]">{c.service_history?.[0]?.service_name}</span>
              <span className="hidden md:inline col-span-2 text-sm">
                <span className="text-emerald-700 font-medium">₹{(c.total_paid || 0).toLocaleString("en-IN")}</span>
                <span className="text-[#5C6661]"> / ₹{(c.total_due || 0).toLocaleString("en-IN")}</span>
              </span>
              <span className="hidden md:inline col-span-2">
                <span className={`text-xs px-2.5 py-1 rounded-full ${c.service_status === "Active" ? "pill-completed" : "pill-pending"}`}>{c.service_status}</span>
              </span>
            </Link>
          ))
        )}
      </div>

      <AddRecordDialog
        open={addOpen}
        kind="customer"
        onClose={() => setAddOpen(false)}
        onCreated={() => setReload((n) => n + 1)}
      />
    </div>
  );
}
